// src/pages/Activity/index.js

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  TablePagination
} from '@mui/material';
import {
  Timeline as ActivityIcon,
  Task as TaskIcon,
  NotificationsActive as NotificationIcon,
  Search as SearchIcon,
  Add as AddIcon,
  CheckCircle as CompletedIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/client';
import DevOnly from '../../components/DevOnly';
import ActivityDisplay from '../../components/ActivityDisplay';

const ActivityCenter = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Fetch activity timeline data with pagination
  const { data: activitiesData, isLoading: activitiesLoading, error: activitiesError } = useQuery({
    queryKey: ['activities', searchTerm, typeFilter, statusFilter, userFilter, page, rowsPerPage],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        params.append('skip', page * rowsPerPage);
        params.append('limit', rowsPerPage);
        if (searchTerm) params.append('search', searchTerm);
        if (typeFilter !== 'all') params.append('type', typeFilter);
        if (statusFilter !== 'all') params.append('status', statusFilter);
        if (userFilter !== 'all') params.append('user', userFilter);
        
        const response = await apiClient.get(`/activities?${params.toString()}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching activities:', error);
        return { activities: [], total: 0 };
      }
    },
    staleTime: 30 * 1000, // 30 seconds - real-time updates
    refetchInterval: 30000, // Refresh every 30 seconds
  });
  
  const activities = activitiesData?.activities || [];
  const totalActivities = activitiesData?.total || 0;

  // Fetch activity statistics
  const { data: activityStats, isLoading: statsLoading } = useQuery({
    queryKey: ['activity-stats'],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/activities/stats');
        console.log('Activity stats response:', response.data);
        return response.data;
      } catch (error) {
        console.error('Error fetching activity stats:', error);
        return {
          totalActivities: 0,
          pendingTasks: 0,
          completedToday: 0,
          overdueItems: 0
        };
      }
    },
    staleTime: 2 * 60 * 1000,
  });

  // Fetch users for filter
  const { data: usersData } = useQuery({
    queryKey: ['users-list'],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/users');
        return response.data;
      } catch (error) {
        console.error('Error fetching users:', error);
        return { users: [] };
      }
    },
    staleTime: 5 * 60 * 1000,
  });
  
  const users = usersData?.users || [];

  // Mutation for completing activities
  const completeMutation = useMutation({
    mutationFn: async (activityId) => {
      const response = await apiClient.post(`/activities/${activityId}/complete`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['activity-stats'] });
    },
  });

  // Helper functions have been moved to ActivityDisplay component

  const handleCompleteActivity = (activityId) => {
    completeMutation.mutate(activityId);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing page size
  };

  // Reset to first page when filters change
  React.useEffect(() => {
    setPage(0);
  }, [searchTerm, typeFilter, statusFilter, userFilter]);

  if (activitiesError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load activity data. Please try again later.
        </Alert>
      </Box>
    );
  }

  const stats = activityStats || {
    totalActivities: 0,
    pendingTasks: 0,
    completedToday: 0,
    overdueItems: 0
  };
  
  console.log('Stats being used:', stats);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Activity Center
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track tasks, monitor real-time activity, and manage notifications
          </Typography>
        </Box>
        <DevOnly>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/activities/create')}
            size="large"
          >
            New Activity
          </Button>
        </DevOnly>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Activities"
            value={stats.totalActivities}
            icon={<ActivityIcon />}
            color="primary"
            isLoading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Pending Tasks"
            value={stats.pendingTasks}
            icon={<TaskIcon />}
            color="warning"
            isLoading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Completed Today"
            value={stats.completedToday}
            icon={<CompletedIcon />}
            color="success"
            isLoading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Overdue Items"
            value={stats.overdueItems}
            icon={<NotificationIcon />}
            color="error"
            isLoading={statsLoading}
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search activities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                label="Type"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="task">Tasks</MenuItem>
                <MenuItem value="call">Calls</MenuItem>
                <MenuItem value="email">Emails</MenuItem>
                <MenuItem value="appointment">Appointments</MenuItem>
                <MenuItem value="payment">Payments</MenuItem>
                <MenuItem value="assignment">Assignments</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="scheduled">Scheduled</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Assigned To</InputLabel>
              <Select
                value={userFilter}
                label="Assigned To"
                onChange={(e) => setUserFilter(e.target.value)}
              >
                <MenuItem value="all">All Users</MenuItem>
                {users && Array.isArray(users) && users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.full_name || user.username || 'Unknown User'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<TaskIcon />}
                onClick={() => navigate('/tasks')}
                fullWidth
              >
                Task Manager
              </Button>
              <Button
                variant="outlined"
                startIcon={<NotificationIcon />}
                onClick={() => navigate('/notifications')}
                fullWidth
              >
                Notifications
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Activity Timeline */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Activity Timeline
          </Typography>
          {totalActivities > 0 && !activitiesLoading && (
            <Typography variant="body2" color="text.secondary">
              Showing {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, totalActivities)} of {totalActivities} activities
            </Typography>
          )}
        </Box>
        
        {activitiesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : activities.length === 0 ? (
          <Alert severity="info">
            No activities found. {searchTerm || typeFilter !== 'all' || statusFilter !== 'all' || userFilter !== 'all' 
              ? 'Try adjusting your filters.' 
              : 'Create your first activity to get started.'}
          </Alert>
        ) : (
          <Box>
            {activities.map((activity) => (
              <Box key={activity.id} sx={{ position: 'relative' }}>
                <ActivityDisplay activity={activity} />
                <Box sx={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 1, pointerEvents: 'none' }}>
                  {activity.status === 'pending' && (
                    <DevOnly>
                      <Button
                        sx={{ pointerEvents: 'auto' }}
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => handleCompleteActivity(activity.id)}
                        disabled={completeMutation.isLoading}
                      >
                        Complete
                      </Button>
                    </DevOnly>
                  )}
                  <DevOnly>
                    <Button
                      sx={{ pointerEvents: 'auto' }}
                      size="small"
                      variant="outlined"
                      onClick={() => navigate(`/activities/${activity.id}/edit`)}
                    >
                      Edit
                    </Button>
                  </DevOnly>
                </Box>
              </Box>
            ))}
          </Box>
        )}
        
        {/* Pagination */}
        {totalActivities > rowsPerPage && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <TablePagination
              component="div"
              count={totalActivities}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, icon, color = 'primary', isLoading = false }) => {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="text.secondary" gutterBottom variant="body2">
              {title}
            </Typography>
            {isLoading ? (
              <CircularProgress size={24} />
            ) : (
              <Typography variant="h4" component="div" color={`${color}.main`}>
                {value}
              </Typography>
            )}
          </Box>
          <Box sx={{ color: `${color}.main` }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ActivityCenter;
